import React, { useState, useEffect } from 'react';
import { Timer, Clock, AlertTriangle, CheckCircle, Users, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { getGameDateTime } from '@/utils/gameStatusUtils';

export interface CountdownPhase {
  name: string;
  description: string;
  deadline: Date;
  icon: React.ReactNode;
  color: string;
}

export interface GameCountdownData {
  currentPhase: string;
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
    expired: boolean;
  };
  nextPhase?: CountdownPhase;
  gameStatus: 'pending' | 'confirmed' | 'ready' | 'active' | 'completed' | 'forfeited';
}

interface GameCountdownTimerProps {
  gameId: string;
  gameDate: string;
  gameTime: string;
  gameStatus: string;
  gameTimezone?: string;
  countdown?: GameCountdownData;
  variant?: 'compact' | 'detailed' | 'card';
  showProgress?: boolean;
  className?: string;
}

export const GameCountdownTimer: React.FC<GameCountdownTimerProps> = ({
  gameId,
  gameDate,
  gameTime,
  gameStatus,
  gameTimezone = 'UTC',
  countdown,
  variant = 'detailed',
  showProgress = true,
  className
}) => {
  const [timeRemaining, setTimeRemaining] = useState(countdown?.timeRemaining);

  // Calculate time remaining in real-time
  useEffect(() => {
    if (!gameDate || !gameTime) return;

    const interval = setInterval(() => {
      const gameDateTime = getGameDateTime(gameDate, gameTime, gameTimezone);
      const now = new Date();
      const diff = gameDateTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          totalSeconds: 0,
          expired: true
        });
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeRemaining({
          days,
          hours,
          minutes,
          seconds,
          totalSeconds: Math.floor(diff / 1000),
          expired: false
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, gameTime, gameTimezone]);

  const getPhaseInfo = () => {
    const phases: CountdownPhase[] = [
      {
        name: 'Setup',
        description: 'Team assignments and roster building',
        deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days before
        icon: <Users className="h-4 w-4" />,
        color: 'bg-blue-500'
      },
      {
        name: 'Confirmation',
        description: 'Final roster confirmation required',
        deadline: new Date(`${gameDate}T${gameTime}`),
        icon: <CheckCircle className="h-4 w-4" />,
        color: 'bg-yellow-500'
      },
      {
        name: 'Ready',
        description: 'Players mark ready-to-play status',
        deadline: new Date(`${gameDate}T${gameTime}`),
        icon: <Clock className="h-4 w-4" />,
        color: 'bg-orange-500'
      },
      {
        name: 'Game Time',
        description: 'Game in progress',
        deadline: new Date(`${gameDate}T${gameTime}`),
        icon: <Play className="h-4 w-4" />,
        color: 'bg-green-500'
      }
    ];

    const currentPhaseIndex = gameStatus === 'pending' ? 0 :
                              gameStatus === 'confirmed' ? 1 :
                              gameStatus === 'ready' ? 2 : 3;
    
    return {
      current: phases[currentPhaseIndex],
      next: phases[currentPhaseIndex + 1],
      progress: (currentPhaseIndex / (phases.length - 1)) * 100
    };
  };

  const formatTime = (compact = false) => {
    if (!timeRemaining) return 'Loading...';
    
    if (timeRemaining.expired) {
      return gameStatus === 'completed' ? 'Game Completed' : 'Time Expired';
    }

    if (compact) {
      if (timeRemaining.days > 0) {
        return `${timeRemaining.days}d ${timeRemaining.hours}h`;
      } else if (timeRemaining.hours > 0) {
        return `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
      } else {
        return `${timeRemaining.minutes}m ${timeRemaining.seconds}s`;
      }
    }

    const parts = [];
    if (timeRemaining.days > 0) parts.push(`${timeRemaining.days} day${timeRemaining.days !== 1 ? 's' : ''}`);
    if (timeRemaining.hours > 0) parts.push(`${timeRemaining.hours} hour${timeRemaining.hours !== 1 ? 's' : ''}`);
    if (timeRemaining.minutes > 0) parts.push(`${timeRemaining.minutes} min`);
    if (timeRemaining.days === 0 && timeRemaining.hours === 0) {
      parts.push(`${timeRemaining.seconds} sec`);
    }
    
    return parts.join(', ');
  };

  const getUrgencyLevel = () => {
    if (!timeRemaining || timeRemaining.expired) return 'expired';
    if (timeRemaining.totalSeconds < 3600) return 'critical'; // < 1 hour
    if (timeRemaining.totalSeconds < 7200) return 'urgent'; // < 2 hours
    if (timeRemaining.totalSeconds < 86400) return 'warning'; // < 1 day
    return 'normal';
  };

  const urgency = getUrgencyLevel();
  const phaseInfo = getPhaseInfo();

  const getStatusIcon = () => {
    switch (urgency) {
      case 'expired': return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'critical': return <Clock className="h-4 w-4 text-red-500 animate-pulse" />;
      case 'urgent': return <Clock className="h-4 w-4 text-orange-500" />;
      case 'warning': return <Timer className="h-4 w-4 text-yellow-500" />;
      default: return <Timer className="h-4 w-4 text-primary" />;
    }
  };

  if (variant === 'compact') {
    return (
      <Badge 
        variant={urgency === 'expired' ? "destructive" : urgency === 'critical' ? "destructive" : "outline"}
        className={cn("flex items-center gap-1", className)}
      >
        {getStatusIcon()}
        {formatTime(true)}
      </Badge>
    );
  }

  if (variant === 'card') {
    return (
      <Card className={cn("w-full", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            {phaseInfo.current.icon}
            Game Countdown - {phaseInfo.current.name} Phase
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <div className="text-2xl font-mono font-bold text-primary mb-1">
              {formatTime()}
            </div>
            <p className="text-sm text-muted-foreground">
              {phaseInfo.current.description}
            </p>
          </div>

          {showProgress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{Math.round(phaseInfo.progress)}%</span>
              </div>
              <Progress value={phaseInfo.progress} className="h-2" />
            </div>
          )}

          {phaseInfo.next && (
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                {phaseInfo.next.icon}
                Next: {phaseInfo.next.name}
              </div>
              <p className="text-xs text-muted-foreground">
                {phaseInfo.next.description}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default detailed variant
  return (
    <div className={cn("bg-card border rounded-lg p-4", className)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="font-medium">Game Countdown</span>
        </div>
        <Badge variant="outline" className="text-xs">
          {phaseInfo.current.name}
        </Badge>
      </div>
      
      <div className="text-center">
        <div className={cn(
          "text-xl font-mono font-bold mb-1",
          urgency === 'critical' && "text-red-500 animate-pulse",
          urgency === 'urgent' && "text-orange-500",
          urgency === 'warning' && "text-yellow-500",
          urgency === 'expired' && "text-destructive"
        )}>
          {formatTime()}
        </div>
        <p className="text-xs text-muted-foreground">
          {timeRemaining?.expired ? 'Game time has passed' : 'until game time'}
        </p>
      </div>

      {showProgress && (
        <div className="mt-3">
          <Progress value={phaseInfo.progress} className="h-1" />
        </div>
      )}
    </div>
  );
};